import { Spinner } from '@change/ds-core-ui';

import type { Locale } from '@change-corgi/config/locales';
import { isSupportedLocale } from '@change-corgi/config/locales';
import { useKnownAvailableParams } from '@change-corgi/core/react/router';

import type { PageSchemaOrgItem } from 'src/shared/prefetch';
import { withPrefetchedData } from 'src/shared/prefetch';

import type { TopicPagePrefetchedDataProps } from 'src/app/pages/topic/pageContext';
import { prefetchPageContext, TopicPageContextProvider } from 'src/app/pages/topic/pageContext';
import { isLoaded } from 'src/app/shared/utils/async';

import { Layout } from '../layout';

import { TopicErrorHandler } from './ErrorHandler';

const OG_IMAGE = 'https://static.change.org/brand-pages/impact/opengraph-impact.jpg';

function upperCaseFirstChar(s: string) {
	return s.charAt(0).toUpperCase() + s.substring(1).toLowerCase();
}

function extractLang(slug: string): string | false {
	const regex: RegExp = /-([a-z]{2})-([a-z]{2})$/i;
	const match = slug.match(regex);

	if (match) {
		const lang = `${match[1].toLowerCase()}-${match[2].toUpperCase()}`;

		if (isSupportedLocale(lang as Locale)) {
			return lang;
		}
	}

	return false;
}

export const Topic = withPrefetchedData(
	function Topic({ prefetchedData }: TopicPagePrefetchedDataProps) {
		const { slug } = useKnownAvailableParams<{ slug: string }>();

		return (
			<TopicPageContextProvider
				slug={slug}
				prefetchedData={prefetchedData}
				errorHandler={TopicErrorHandler}
				loadingFallback={<Spinner className="mx-auto my-64" />}
			>
				<Layout />
			</TopicPageContextProvider>
		);
	},
	{
		prefetchName: __MODULE_HASH__,
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		prefetchData: async (context) => prefetchPageContext(context.params.slug!, context),
		pageTitle: ({ prefetchedData, i18n, fromTemplate }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			const title = upperCaseFirstChar(prefetchedData.pageData.pageData.name);
			return fromTemplate(i18n.translate('corgi.topic_page.page_meta.title', { title }));
		},
		pageCanonical: ({ prefetchedData }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			return `/topic/${prefetchedData.pageData.pageData.slug}`;
		},
		pageMetadata: ({ prefetchedData, i18n }) => {
			if (
				!isLoaded(prefetchedData.pageData) ||
				!isLoaded(prefetchedData.recentPetitionsPaginatedData) ||
				!isLoaded(prefetchedData.trendingPetitionsPaginatedData)
			) {
				return undefined;
			}
			const { name, photos } = prefetchedData.pageData.pageData;
			const title = upperCaseFirstChar(name);
			const translatedTitle = i18n.translate('corgi.topic_page.page_meta.title', { title });
			const translatedDescription = i18n.translate('corgi.topic_page.page_meta.description', { title });
			const image = photos[0]?.heroMedium.url ?? 'https://static.change.org/images/default-petition.gif';

			const indexed = !!(
				prefetchedData.recentPetitionsPaginatedData.recentPetitionsPaginatedData.totalCount ||
				prefetchedData.trendingPetitionsPaginatedData.trendingPetitionsPaginatedData.totalCount
			);

			return {
				description: translatedDescription,
				ogImage: image,
				ogTitle: translatedTitle,
				ogDescription: translatedDescription,
				twitterImage: image,
				twitterImageAlt: OG_IMAGE,
				twitterTitle: translatedTitle,
				twitterDescription: translatedDescription,
				ogType: 'website' as const,
				...(!indexed && { robots: 'noindex' }),
			};
		},
		pageSchemaOrg: ({ prefetchedData, defineQuestion, defineItemList, defineListItem, resolveUrl }) => {
			if (!isLoaded(prefetchedData.pageData) || !isLoaded(prefetchedData.trendingPetitionsPaginatedData)) {
				return undefined;
			}

			const results: PageSchemaOrgItem[] = [];

			const { structuredData, name } = prefetchedData.pageData.pageData;
			if (structuredData?.length) {
				const questions = structuredData.map(({ content, title }) =>
					defineQuestion({
						name: title,
						acceptedAnswer: content,
					}),
				);
				if (questions.length) {
					// unhead does not provide a way to define a FAQPage, so we need to define it manually
					results.push({
						'@type': 'FAQPage',
						// unhead does not support arrays of questions even though they should be supportes
						// https://developers.google.com/search/docs/appearance/structured-data/faqpage
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
						mainEntity: questions as any,
					});
				}
			}

			const { items, totalCount } = prefetchedData.trendingPetitionsPaginatedData.trendingPetitionsPaginatedData;
			if (items.length) {
				results.push(
					defineItemList({
						// FIXME: i18n
						name: `Petitions in ${name}`,
						numberOfItems: totalCount,
						itemListElement: items.map((item, index) =>
							defineListItem({
								position: index + 1,
								url: resolveUrl(`/p/${item.node.slug}`),
								name: item.node.ask,
								description: item.node.description,
							}),
						),
					}),
				);
			}

			return results.length ? results : undefined;
		},
		pageAlternateUrls: ({ prefetchedData }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			const { slug } = prefetchedData.pageData.pageData;
			const lang = extractLang(slug);
			if (!lang) {
				return undefined;
			}
			return { [lang]: `/topic/${slug}` };
		},
	},
);
